/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\pages\Devis\ConfigurateurArticle\ArticleConfigurationCompact.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
SELLERIE MARINE - COMPOSANT CONFIGURATION ARTICLE COMPACTE

📋 DESCRIPTION:
Composant React de configuration compacte des articles avec interface utilisateur optimisée.
Formulaire responsive pour paramétrage complet des articles (coussins et bâches).

🎯 RESPONSABILITÉS:
- Interface de configuration complète des articles (identification, dimensions, options)
- Sélection visuelle du type de produit avec icônes (coussin/bâche)
- Gestion adaptative des dimensions selon la forme sélectionnée
- Configuration des options spécialisées par type (mousses, fermetures, accessoires)
- Interface responsive avec layout adaptatif (mobile/desktop)
- Intégration du service mousses pour sélection dynamique

🔗 DÉPENDANCES:
- types.ts: Interfaces Article et Matiere
- MousseService: Catalogue dynamique des mousses disponibles
- React: Hooks useState, useEffect pour gestion d'état
- Tailwind CSS: Classes utilitaires pour interface responsive

📡 INTERACTIONS:
- Parent: Communication des modifications via callbacks handleChange et onDataChange
- MousseService: Chargement dynamique du catalogue mousses
- Interface: Validation en temps réel des saisies utilisateur
- Responsive: Adaptation automatique mobile/desktop

📊 STATUT: ACTIF (Production)
📅 Dernière mise à jour: Juin 2025
👤 Responsable: Équipe développement

💡 NOTES:
- Interface responsive avec grille adaptative selon écran
- Icônes SVG personnalisées pour sélection visuelle des types
- Chargement dynamique des mousses avec fallback sur types génériques
- Validation automatique des dimensions selon forme sélectionnée
- Options spécialisées par type (coussin vs bâche)
- Gestion des états de chargement pour meilleure UX
*/
import React, { useState, useEffect } from 'react';
import { Article, Matiere } from './types';
import { mousseService } from '../../../services/MousseService';
import TypeForme from './TypeForme';

interface ArticleConfigurationCompactProps {
  article: Article;
  matieres: Matiere[];
  handleChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    section?: string,
    subsection?: string
  ) => void;
  onDataChange: () => void;
}

// Composant icône Coussin 3D
const CoussinIconButton = ({ selected, onClick }: { selected: boolean; onClick: () => void }) => (
  <button
    onClick={onClick}
    className={`flex flex-col items-center p-3 border rounded-lg transition-colors ${
      selected ? 'border-blue-600 bg-blue-50' : 'border-gray-300 bg-white'
    }`}
  >
    <svg
      viewBox="0 0 64 64"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="w-10 h-10 mb-2"
    >
      <path d="M12 20 c0 -8 6 -14 14 -14 h12 c8 0 14 6 14 14 v24 c0 8 -6 14 -14 14 H26 c-8 0 -14 -6 -14 -14 Z" />
      <path d="M20 32 h24" strokeWidth="4" />
    </svg>
    <span className="text-sm font-medium">Coussin</span>
  </button>
);

// Composant icône Bâche
const BacheIconButton = ({ selected, onClick }: { selected: boolean; onClick: () => void }) => (
  <button
    onClick={onClick}
    className={`flex flex-col items-center p-3 border rounded-lg transition-colors ${
      selected ? 'border-blue-600 bg-blue-50' : 'border-gray-300 bg-white'
    }`}
  >
    <svg
      viewBox="0 0 64 64"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="w-10 h-10 mb-2"
    >
      <rect x="8" y="16" width="48" height="32" rx="2" />
      <circle cx="16" cy="24" r="2" fill="currentColor" />
      <circle cx="48" cy="24" r="2" fill="currentColor" />
      <circle cx="16" cy="40" r="2" fill="currentColor" />
      <circle cx="48" cy="40" r="2" fill="currentColor" />
    </svg>
    <span className="text-sm font-medium">Bâche</span>
  </button>
);

const ArticleConfigurationCompact: React.FC<ArticleConfigurationCompactProps> = ({
  article,
  matieres,
  handleChange,
  onDataChange
}) => {
  // Fonction utilitaire pour détecter les formes rondes
  const isFormeRonde = (forme: string): boolean => {
    return forme === 'rond_avec_bandeau' || forme === 'rond_bandeau';
  };

  // État pour les mousses disponibles
  const [mousses, setMousses] = useState<any[]>([]);
  const [moussesLoading, setMoussesLoading] = useState(false);
  
  // État pour la modal d'aperçu agrandi
  const [showLargePreview, setShowLargePreview] = useState(false);
  
  // État pour la validation des dimensions biseau
  const [erreurBiseau, setErreurBiseau] = useState<string>('');

  // Charger les mousses au montage du composant
  useEffect(() => {
    const loadMousses = async () => {
      try {
        setMoussesLoading(true);
        const moussesData = await mousseService.getMousses();
        setMousses(moussesData);
      } catch (error) {
        console.error('Erreur lors du chargement des mousses:', error);
        // En cas d'erreur, utiliser les types génériques par défaut
        setMousses([]);
      } finally {
        setMoussesLoading(false);
      }
    };

    loadMousses();
  }, []);

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-lg font-medium mb-6">Configuration de l'article</h2>
      
      {/* Layout responsive : 1 colonne sur mobile, 2 colonnes sur desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Colonne gauche : Identification + Type & Forme */}
        <div className="space-y-6">
          {/* Section Identification */}
          <div>
            <h3 className="flex items-center text-md font-medium text-gray-900 mb-4">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Identification
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-1">
                  Nom de l'article
                </label>
                <input
                  type="text"
                  id="nom"
                  name="nom"
                  value={article.nom}
                  onChange={(e) => {
                    handleChange(e);
                    onDataChange();
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Nom de l'article"
                />
              </div>
              <div>
                <label htmlFor="quantite" className="block text-sm font-medium text-gray-700 mb-1">
                  Quantité
                </label>
                <input
                  type="number"
                  id="quantite"
                  name="quantite"
                  value={article.quantite}
                  onChange={(e) => {
                    handleChange(e);
                    onDataChange();
                  }}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Section Type & Forme - NOUVEAU COMPOSANT */}
          <TypeForme
            article={article}
            handleChange={handleChange}
            onDataChange={onDataChange}
          />
        </div>

        {/* Colonne droite : Dimensions + Options */}
        <div className="space-y-6">
          {/* Section Dimensions avec visualisation dynamique */}
          {article.forme && (
            <div>
              <h3 className="flex items-center text-md font-medium text-gray-900 mb-4">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Dimensions
              </h3>
              
              {/* Visualisation avec image et champs de saisie */}
              <div className="flex flex-col lg:flex-row gap-6">
                {/* Zone des champs de saisie */}
                <div className="flex-1">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Interface spécifique pour les formes biseau */}
                {(article.forme === 'rectangle_joues_biseau' || article.forme === 'rectangle_demi_bandeau_biseau') ? (
                  <div className="col-span-2 space-y-4">
                    {/* Titre spécial pour formes biseau */}
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                      <h4 className="text-sm font-medium text-orange-800 mb-1">
                        🔶 Forme avec biseau - Dimensions spécialisées
                      </h4>
                      <p className="text-xs text-orange-600">
                        Largeur dessous &gt; largeur dessus pour créer l'effet biseau
                      </p>
                    </div>

                    {/* Grille des champs biseau */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Longueur (L) */}
                      <div className="flex flex-col">
                        <label htmlFor="longueur" className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                          <span className="w-3 h-3 rounded-full bg-red-500"></span>
                          Longueur L (cm)
                        </label>
                        <input
                          type="number"
                          id="longueur"
                          name="longueur"
                          className="w-full px-3 py-2 border border-red-300 bg-red-50 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-500 transition-colors"
                          value={article.dimensions.longueur || ''}
                          onChange={(e) => {
                            handleChange(e, 'dimensions');
                            onDataChange();
                          }}
                          min="10"
                          max="9999"
                          step="0.1"
                          required
                          placeholder="Longueur constante"
                        />
                      </div>

                      {/* Largeur dessus (l1) */}
                      <div className="flex flex-col">
                        <label htmlFor="largeur_dessus" className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                          <span className="w-3 h-3 rounded-full bg-pink-500"></span>
                          Largeur dessus l1 (cm)
                        </label>
                        <input
                          type="number"
                          id="largeur_dessus"
                          name="largeur_dessus"
                          className="w-full px-3 py-2 border border-pink-300 bg-pink-50 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-pink-200 focus:border-pink-500 transition-colors"
                          value={article.dimensions.largeur_dessus || ''}
                          onChange={(e) => {
                            const l1 = parseFloat(e.target.value) || 0;
                            const l2 = parseFloat(String(article.dimensions.largeur_dessous || '0')) || 0;
                            
                            // Validation biseau : l2 doit être > l1
                            if (l1 > 0 && l2 > 0 && l2 <= l1) {
                              setErreurBiseau('⚠️ Erreur biseau : la largeur dessous (l2) doit être supérieure à la largeur dessus (l1)');
                            } else {
                              setErreurBiseau('');
                            }
                            
                            handleChange(e, 'dimensions');
                            onDataChange();
                          }}
                          min="10"
                          max="9999"
                          step="0.1"
                          required
                          placeholder="Largeur petite (dessus)"
                        />
                      </div>

                      {/* Largeur dessous (l2) */}
                      <div className="flex flex-col">
                        <label htmlFor="largeur_dessous" className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                          <span className="w-3 h-3 rounded-full bg-green-500"></span>
                          Largeur dessous l2 (cm)
                        </label>
                        <input
                          type="number"
                          id="largeur_dessous"
                          name="largeur_dessous"
                          className="w-full px-3 py-2 border border-green-300 bg-green-50 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-green-500 transition-colors"
                          value={article.dimensions.largeur_dessous || ''}
                          onChange={(e) => {
                            const l2 = parseFloat(e.target.value) || 0;
                            const l1 = parseFloat(String(article.dimensions.largeur_dessus || '0')) || 0;
                            
                            // Validation biseau : l2 doit être > l1
                            if (l1 > 0 && l2 > 0 && l2 <= l1) {
                              setErreurBiseau('⚠️ Erreur biseau : la largeur dessous (l2) doit être supérieure à la largeur dessus (l1)');
                            } else {
                              setErreurBiseau('');
                            }
                            
                            handleChange(e, 'dimensions');
                            onDataChange();
                          }}
                          min="10"
                          max="9999"
                          step="0.1"
                          required
                          placeholder="Largeur grande (dessous)"
                        />
                      </div>

                      {/* Hauteur (H) */}
                      <div className="flex flex-col">
                        <label htmlFor="hauteur" className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                          <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                          Hauteur H (cm)
                        </label>
                        <input
                          type="number"
                          id="hauteur"
                          name="hauteur"
                          className="w-full px-3 py-2 border border-blue-300 bg-blue-50 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 transition-colors"
                          value={article.dimensions.hauteur || ''}
                          onChange={(e) => {
                            handleChange(e, 'dimensions');
                            onDataChange();
                          }}
                          min="2"
                          max="9999"
                          step="0.1"
                          required
                          placeholder="Hauteur du biseau"
                        />
                      </div>
                    </div>

                    {/* Message d'erreur biseau */}
                    {erreurBiseau && (
                      <div className="col-span-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center">
                          <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                          <span className="text-sm text-red-700 font-medium">{erreurBiseau}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                /* Dimensions selon la forme avec codes couleur */
                (article.forme === 'rectangulaire_bandeau' || article.forme === 'rectangulaire_demi_bandeau' || article.forme === 'rectangulaire_joues' || article.forme === 'rectangle_boite' || article.forme === 'rectangle_simple') && (
                  <>
                    <div className="flex flex-col">
                      <label htmlFor="longueur" className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                        <span className="w-3 h-3 rounded-full bg-red-500"></span>
                        Longueur (cm)
                      </label>
                      <input
                        type="number"
                        id="longueur"
                        name="longueur"
                        value={article.dimensions.longueur || ''}
                        onChange={(e) => {
                          handleChange(e, 'dimensions');
                          onDataChange();
                        }}
                        className="w-20 px-3 py-2 border border-red-500 bg-red-50 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-600 transition-colors"
                        min="1"
                        max="9999"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label htmlFor="largeur" className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                        <span className="w-3 h-3 rounded-full bg-green-500"></span>
                        Largeur (cm)
                      </label>
                      <input
                        type="number"
                        id="largeur"
                        name="largeur"
                        value={article.dimensions.largeur || ''}
                        onChange={(e) => {
                          handleChange(e, 'dimensions');
                          onDataChange();
                        }}
                        className="w-20 px-3 py-2 border border-green-500 bg-green-50 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-green-600 transition-colors"
                        min="1"
                        max="9999"
                      />
                    </div>
                  </>
                ))}

                {/* Diamètre pour formes rondes et cylindres */}
                {(article.forme.includes('rond') || article.forme === 'cylindre_joues') && (
                  <div className="flex flex-col">
                    <label htmlFor="diametre" className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                      <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                      Diamètre (cm)
                    </label>
                    <input
                      type="number"
                      id="diametre"
                      name="diametre"
                      value={article.dimensions.diametre || (article.forme === 'rond_avec_bandeau' ? 50 : '')}
                      onChange={(e) => {
                        handleChange(e, 'dimensions');
                        onDataChange();
                      }}
                      className="w-20 px-3 py-2 border border-yellow-500 bg-yellow-50 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-yellow-200 focus:border-yellow-600 transition-colors"
                      min="1"
                      max="9999"
                    />
                  </div>
                )}

                {/* Hauteur selon la forme */}
                {(article.forme === 'rectangulaire_bandeau' || article.forme === 'rectangulaire_demi_bandeau' || article.forme === 'rectangulaire_joues' || article.forme === 'rectangle_boite' || article.forme.includes('rond')) && (
                  <div className="flex flex-col">
                    <label htmlFor="hauteur" className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                      <span className="w-3 h-3 rounded-full bg-green-500"></span>
                      Hauteur (cm)
                    </label>
                    <input
                      type="number"
                      id="hauteur"
                      name="hauteur"
                      value={article.dimensions.hauteur || (
                        article.forme === 'rectangle_boite' ? 10 :
                        article.forme === 'rectangulaire_demi_bandeau' ? 10 :
                        article.forme === 'rond_avec_bandeau' ? 10 : ''
                      )}
                      onChange={(e) => {
                        handleChange(e, 'dimensions');
                        onDataChange();
                      }}
                      className="w-20 px-3 py-2 border border-green-500 bg-green-50 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-green-600 transition-colors"
                      min="1"
                      max="9999"
                    />
                  </div>
                )}

                {/* Longueur pour cylindres */}
                {article.forme === 'cylindre_joues' && (
                  <div className="flex flex-col">
                    <label htmlFor="longueur" className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                      <span className="w-3 h-3 rounded-full bg-red-500"></span>
                      Longueur (cm)
                    </label>
                    <input
                      type="number"
                      id="longueur"
                      name="longueur"
                      value={article.dimensions.longueur || ''}
                      onChange={(e) => {
                        handleChange(e, 'dimensions');
                        onDataChange();
                      }}
                      className="w-20 px-3 py-2 border border-red-500 bg-red-50 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-600 transition-colors"
                      min="1"
                      max="9999"
                    />
                  </div>
                )}
                  </div>

                  <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                    <div className="font-semibold">💡 Conseil de saisie des mesures</div>
                    <p className="mt-1">
                      Selon l’élasticité de la matière et la densité / l’épaisseur de la mousse, il peut être pertinent d’ajuster à la
                      baisse les dimensions saisies afin d’anticiper la déformation sous contrainte.
                    </p>
                    <p className="mt-1">Le talon s’ajoute systématiquement à ces dimensions.</p>
                    <p className="mt-1">
                      Les valeurs renseignées servent de base au plan atelier, aux temps de couture, aux quantités de matière et au{" "}
                      <strong>dimensionnement des mousses</strong>.
                    </p>
                  </div>
                </div>

                {/* Zone d'affichage de l'image avec flèches */}
                {(() => {
                  const getFormeImage = (forme: string, fond: boolean | undefined): string | null => {
                    const formeImages: { [key: string]: string } = {
                      'rectangulaire_bandeau': 'rectanglulaire_bandeau_fleches.png',
                      'rectangulaire_demi_bandeau': 'rectanglulaire_demi_bandeau_fleches.png',
                      'rectangulaire_joues': 'rectanglulaire_joues_fleche.png',
                      'rectangle_joues_biseau': 'Rectangle_joues_biseau_fleches.png',
                      'rectangle_demi_bandeau_biseau': 'rectangle_demi_bandeau_biseau_fleches.png',
                      'rectangle_boite': 'rectangle_boite_fleches.png',
                      'rond_avec_bandeau': 'rond_avec_bandeau_fleches.png',
                      'cylindre_joues': 'cylindre_joues_fleches.png'
                    };
                    const formeImagesSansFond: { [key: string]: string } = {
                      'rectangulaire_bandeau': 'rectanglulaire_bandeau_fleches_sans_fond.png',
                      'rectangulaire_demi_bandeau': 'rectanglulaire_demi_bandeau_fleches_sans_fond.png',
                      'rectangulaire_joues': 'rectanglulaire_joues_fleche_sans_fond.png',
                      'rectangle_joues_biseau': 'Rectangle_joues_biseau_fleches_sans_fond.png',
                      'rectangle_demi_bandeau_biseau': 'rectangle_demi_bandeau_biseau_fleches_sans_fond.png',
                      'rectangle_boite': 'rectangle_boite_fleches_sans_fond.png',
                      'rond_avec_bandeau': 'rond_avec_bandeau_fleches_sans_fond.png',
                    };
                    if (fond === false && formeImagesSansFond[forme]) {
                      return formeImagesSansFond[forme];
                    }
                    return formeImages[forme] || null;
                  };

                  const formeImage = getFormeImage(article.forme, article.options?.fond);
                  
                  return formeImage ? (
                    <div className="flex-shrink-0 flex justify-center lg:justify-end">
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <img
                          src={`/images/formes/coussins/${formeImage}`}
                          alt={`Schéma ${article.forme} avec flèches dimensionnelles`}
                          className="w-48 h-auto object-contain"
                          style={{ maxHeight: '200px' }}
                        />
                        <p className="text-xs text-gray-500 text-center mt-2">
                          Correspondance couleurs champs/flèches
                        </p>
                      </div>
                    </div>
                  ) : null;
                })()}
              </div>
            </div>
          )}

          {/* Section Options - SEULEMENT SI UNE FORME EST SÉLECTIONNÉE */}
          {article.forme && (
            <div>
              <h3 className="flex items-center text-md font-medium text-gray-900 mb-4">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Options
              </h3>
              
              {article.type === 'coussin' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-3">
                  {/* Option Fond - Masquée pour cylindre avec joues */}
                  {article.forme !== 'cylindre_joues' && (
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="fond"
                        checked={article.options.fond}
                        disabled={article.options.grille} // ✅ PROTECTION : Désactiver si grille active
                        onChange={(e) => {
                          // ✅ PROTECTION : Empêcher désactivation fond si grille active
                          if (article.options.grille && !e.target.checked) {
                            alert("⚠️ Impossible de désactiver le fond : l'option grille nécessite obligatoirement un fond.\n\nVeuillez d'abord désactiver la grille de drainage.");
                            return;
                          }
                          handleChange(e, 'options');
                          onDataChange();
                        }}
                        className={`mr-2 ${article.options.grille ? 'opacity-50 cursor-not-allowed' : ''}`}
                      />
                      <span className={article.options.grille ? 'text-gray-400' : ''}>
                        Fond
                        {article.options.grille && (
                          <span className="text-xs text-orange-600 ml-1">(obligatoire)</span>
                        )}
                      </span>
                    </label>
                  )}
                  
                  {/* Option Grille - Masquée pour cylindre avec joues */}
                  {article.forme !== 'cylindre_joues' && (
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="grille"
                        checked={article.options.grille}
                        disabled={!article.options.fond} // ✅ PROTECTION : Désactiver si pas de fond
                        onChange={(e) => {
                          // ✅ ACTIVATION AUTOMATIQUE : Fond + fermeture si grille activée
                          if (e.target.checked) {
                            // 1. Activer automatiquement le fond si pas déjà activé
                            if (!article.options.fond) {
                              const fondEvent = {
                                target: {
                                  name: 'fond',
                                  checked: true,
                                  type: 'checkbox'
                                }
                              } as unknown as React.ChangeEvent<HTMLInputElement>;
                              handleChange(fondEvent, 'options');
                            }
                            
                            // 2. Activer automatiquement la fermeture si pas de fermeture
                            if (!article.options.fermeture) {
                              const fermetureEvent = {
                                target: {
                                  name: 'fermeture',
                                  value: 'glissiere',
                                  type: 'select'
                                }
                              } as unknown as React.ChangeEvent<HTMLSelectElement>;
                              handleChange(fermetureEvent, 'options');
                            }
                            
                            // 3. ✅ NOUVEAU: Définir le mode ZIP en "1/2 périmètre" pour les formes rondes
                            if (article.forme === 'rond_avec_bandeau' || article.forme === 'rond_bandeau') {
                              const typeZipEvent = {
                                target: {
                                  name: 'type_zip',
                                  value: 'demi_perimetre',
                                  type: 'select'
                                }
                              } as unknown as React.ChangeEvent<HTMLSelectElement>;
                              handleChange(typeZipEvent, 'options');
                            }
                          }
                          handleChange(e, 'options');
                          onDataChange();
                        }}
                        className="mr-2"
                      />
                      <span>
                        Grille de drainage
                        {!article.options.fond && (
                          <span className="text-xs text-gray-500 ml-1">(nécessite fond)</span>
                        )}
                      </span>
                    </label>
                  )}

                  {/* Option Supplément de bord (ancien talon d'agrafage) */}
                  {article.forme !== 'cylindre_joues' && (
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="talonAgrafageActif"
                          checked={article.options.talonAgrafageActif || false}
                          disabled={article.options.fond || article.options.grille}
                          onChange={(e) => {
                            handleChange(e, 'options');
                            onDataChange();
                          }}
                          className={`mr-2 ${article.options.fond || article.options.grille ? 'opacity-50 cursor-not-allowed' : ''}`}
                        />
                        <span className={article.options.fond || article.options.grille ? 'text-gray-400' : ''}>
                          Supplément de bord
                          <span className="block text-xs text-gray-500">
                            Ajoute de la matière sur le bord ouvert pour une finition de type agrafage, ourlet ou fourreau.
                          </span>
                          {(article.options.fond || article.options.grille) && (
                            <span className="text-xs text-gray-500 ml-1">(actif uniquement si fond et grille désactivés)</span>
                          )}
                        </span>
                      </label>
                      <div className="flex items-center space-x-2 ml-6">
                        <input
                          type="number"
                          name="talonAgrafage"
                          min={1}
                          max={8}
                          step={0.5}
                          value={article.options.talonAgrafage ?? 3}
                          disabled={!article.options.talonAgrafageActif || article.options.fond || article.options.grille}
                          onChange={(e) => {
                            handleChange(e, 'options');
                            onDataChange();
                          }}
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                        <span className="text-xs text-gray-500">cm</span>
                      </div>
                    </div>
                  )}

                  {/* Aperçu visuel composition fond avec grille */}
                  {article.options.grille && (
                    <div className="ml-6 mt-3 p-3 bg-gray-50 rounded-md border">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs font-medium text-gray-900">
                          🔍 Aperçu composition du fond {isFormeRonde(article.forme) ? 'circulaire' : 'rectangulaire'}
                        </h4>
                        <button
                          onClick={() => setShowLargePreview(true)}
                          className="flex items-center text-xs text-blue-600 hover:text-blue-800 transition-colors"
                          title="Agrandir l'aperçu"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                          </svg>
                          Agrandir
                        </button>
                      </div>
                      
                      {/* Schéma visuel adaptatif selon la forme */}
                      <div className="bg-white border-2 border-gray-300 rounded-lg p-2 mb-2 cursor-pointer hover:border-blue-400 transition-colors"
                           onClick={() => setShowLargePreview(true)}
                           title="Cliquer pour agrandir">
                        
                        {isFormeRonde(article.forme) ? (
                          // === FORME RONDE ===
                          <div className="flex items-center justify-center bg-gray-100 rounded-lg border border-gray-400" style={{height: '120px'}}>
                            <div className="relative">
                              {/* Fond circulaire complet en mesh - TAILLE PROPORTIONNELLE AU DIAMÈTRE RÉEL */}
                              {(() => {
                                const diametre = article.dimensions.diametre || 50;
                                // Calculer la taille du mesh proportionnellement (max 96px pour diamètre 50cm)
                                const tailleMesh = Math.min(96, (diametre / 50) * 96);
                                
                                return (
                                  <div 
                                    className="bg-gray-200 border-2 border-gray-500 rounded-full shadow-sm relative"
                                    style={{
                                      width: `${tailleMesh}px`,
                                      height: `${tailleMesh}px`
                                    }}
                                  >
                                    {/* Motif mesh circulaire - VISIBLE sur toute la surface */}
                                    <div className="absolute inset-2 rounded-full overflow-hidden">
                                      <div className="grid grid-cols-6 grid-rows-6 gap-0.5 h-full">
                                        {Array.from({length: 36}).map((_, i) => (
                                          <div 
                                            key={i} 
                                            className="rounded-full bg-gray-400"
                                          ></div>
                                        ))}
                                      </div>
                                    </div>
                                    
                                    {/* Fermeture en arc qui suit le périmètre du mesh - 1/2 périmètre en haut */}
                                    {(() => {
                                      const diametre = article.dimensions.diametre || 50;
                                      const largeurFermeture = 2.0; // 2cm
                                      
                                      // Calculer le rayon mesh proportionnel à la taille réelle
                                      const rayonMesh = tailleMesh / 2 - 4; // Rayon moins l'inset
                                      
                                      // CORRECTION : Arc qui suit le périmètre du mesh - 1/2 périmètre
                                      const rayonFermeture = rayonMesh; // Même rayon que le mesh
                                      const epaisseurFAG = Math.max(3, (largeurFermeture / diametre) * (rayonMesh * 2)); // Épaisseur FAG en pixels (min 3px)
                                      
                                      return (
                                        <div 
                                          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10"
                                          style={{
                                            width: `${rayonFermeture * 2}px`,
                                            height: `${rayonFermeture * 2}px`
                                          }}
                                        >
                                          {/* Demi-cercle supérieur pour la FAG */}
                                          <div 
                                            className="absolute"
                                            style={{
                                              top: '0px',
                                              left: '0px',
                                              width: `${rayonFermeture * 2}px`,
                                              height: `${rayonFermeture}px`,
                                              borderTop: `${epaisseurFAG}px solid #fbbf24`,
                                              borderLeft: `${epaisseurFAG}px solid #fbbf24`,
                                              borderRight: `${epaisseurFAG}px solid #fbbf24`,
                                              borderTopLeftRadius: `${rayonFermeture}px`,
                                              borderTopRightRadius: `${rayonFermeture}px`,
                                              borderBottom: 'none'
                                            }}
                                          ></div>
                                        </div>
                                      );
                                    })()}
                                    
                                    {/* Label central */}
                                    <div className="absolute inset-0 flex items-center justify-center">
                                      <span className="text-xs text-gray-700 font-medium bg-white/80 px-1 rounded">
                                        Mesh
                                      </span>
                                    </div>
                                  </div>
                                );
                              })()}
                              
                              {/* Labels */}
                              <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs text-gray-700 whitespace-nowrap font-medium">
                                Fond mesh complet
                              </div>
                              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 text-xs text-yellow-700 font-medium whitespace-nowrap">
                                Zip 1/2 périmètre
                              </div>
                            </div>
                          </div>
                        ) : (
                          // === FORMES RECTANGULAIRES ===
                          <div className="flex flex-col bg-gray-100 rounded-lg border border-gray-400 overflow-hidden" style={{height: '120px'}}>
                            {(() => {
                              // CALCULS CORRECTS SELON LOGIQUE MÉTIER
                              const longueur = article.dimensions.longueur || 60;
                              const largeur = article.dimensions.largeur || 30;
                              
                              // Dimension principale et secondaire
                              const dimensionPrincipale = Math.max(longueur, largeur);
                              const dimensionSecondaire = Math.min(longueur, largeur);
                              
                              // Calculs corrects : bandes minimum 3cm
                              const largeurFermeture = 2.0; // Fixe : 2cm
                              const largeurBandeMin = 3.0; // Minimum : 3cm par bande
                              
                              // Calculer la largeur des bandes : minimum 3cm, mais peut être plus si nécessaire
                              const espaceDisponible = dimensionSecondaire - largeurFermeture; // 30 - 2 = 28cm
                              const grilleLargeurIdeal = espaceDisponible * 0.8; // 80% de l'espace disponible = 22.4cm
                              const espaceBandes = espaceDisponible - grilleLargeurIdeal; // 28 - 22.4 = 5.6cm
                              const largeurBande = Math.max(largeurBandeMin, espaceBandes / 3); // max(3, 1.87) = 3cm
                              
                              // Recalculer la grille avec les bandes minimum
                              const grilleLargeur = dimensionSecondaire - (3 * largeurBande) - largeurFermeture;
                              
                              // Hauteur totale = dimension secondaire originale (30cm)
                              const hauteurTotale = dimensionSecondaire;
                              
                              // Proportions exactes en %
                              const propBande1 = (largeurBande / hauteurTotale) * 100;
                              const propGrille = (grilleLargeur / hauteurTotale) * 100;
                              const propBande2 = (largeurBande / hauteurTotale) * 100;
                              const propFermeture = (largeurFermeture / hauteurTotale) * 100;
                              const propBande3 = (largeurBande / hauteurTotale) * 100;
                              
                              return (
                                <>
                                  {/* Bande fond 1 - Proportion exacte backend */}
                                  <div className="flex items-center justify-center bg-teal-400 border-b border-teal-600 text-white font-bold text-xs" 
                                       style={{height: `${propBande1}%`, minHeight: '8px'}}>
                                    <span>Bande 1</span>
                                  </div>
                                  
                                  {/* Mesh central - 80% dimension secondaire */}
                                  <div className="flex flex-col items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 border-b-2 border-gray-400 relative" 
                                       style={{height: `${propGrille}%`, minHeight: '40px'}}>
                                    {/* Motif mesh réaliste avec texture marine */}
                                    <div className="grid grid-cols-8 grid-rows-3 gap-0.5 p-1 w-full h-full">
                                      {Array.from({length: 24}).map((_, i) => (
                                        <div key={i} className="bg-gradient-to-br from-slate-400 via-slate-500 to-slate-600 rounded-sm shadow-sm border border-slate-300"></div>
                                      ))}
                                    </div>
                                    <span className="absolute bottom-1 text-xs text-gray-700 font-medium bg-white/80 px-1 rounded">
                                      Mesh drainage
                                    </span>
                                  </div>
                                  
                                  {/* Bande fond 2 - Même proportion que Bande 1 */}
                                  <div className="flex items-center justify-center bg-teal-400 border-b border-teal-600 text-white font-bold text-xs" 
                                       style={{height: `${propBande2}%`, minHeight: '8px'}}>
                                    <span>Bande 2</span>
                                  </div>
                                  
                                  {/* Fermeture - 2cm fixe - MÊME LONGUEUR QUE LES BANDES (dimension principale) */}
                                  <div className="flex items-center bg-white border-b-2 border-gray-500 relative" 
                                       style={{height: `${propFermeture}%`, minHeight: '6px'}}>
                                    {/* Dents de fermeture s'étendant sur TOUTE la largeur */}
                                    <div className="flex flex-row justify-between items-center w-full h-full px-1">
                                      {Array.from({length: 20}).map((_, i) => (
                                        <div key={i} className="w-0.5 h-1 bg-gray-400 rounded"></div>
                                      ))}
                                    </div>
                                    <span className="absolute right-1 top-1/2 transform -translate-y-1/2 text-xs text-gray-700 font-medium bg-white px-1">
                                      Zip
                                    </span>
                                  </div>
                                  
                                  {/* Bande fond 3 - Même proportion que Bandes 1 et 2 */}
                                  <div className="flex items-center justify-center bg-teal-400 text-white font-bold text-xs rounded-b-lg" 
                                       style={{height: `${propBande3}%`, minHeight: '8px'}}>
                                    <span>Bande 3</span>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                      
                      {/* Légende détaillée adaptative selon la forme */}
                      <div className="space-y-1">
                        <div className="text-xs text-gray-600 space-y-0.5">
                          {isFormeRonde(article.forme) ? (
                            // Légende pour forme ronde
                            <>
                              <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 bg-gray-400 border border-gray-500 rounded-full flex-shrink-0"></div>
                                <span><strong>Fond mesh complet :</strong> Matière mesh - ⌀{article.dimensions.diametre || 50}cm</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 bg-yellow-300 border border-yellow-500 flex-shrink-0"></div>
                                <span><strong>Fermeture à glissière :</strong> {article.options.fermeture} sur 1/2 périmètre</span>
                              </div>
                            </>
                          ) : (
                            // Légende pour formes rectangulaires
                            <>
                              <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 bg-teal-400 border border-teal-600 flex-shrink-0 rounded"></div>
                                {(() => {
                                  const longueur = article.dimensions.longueur || 60;
                                  const largeur = article.dimensions.largeur || 30;
                                  const dimensionPrincipale = Math.max(longueur, largeur);
                                  const dimensionSecondaire = Math.min(longueur, largeur);
                                  
                                  // Calculs corrects : bandes minimum 3cm
                                  const largeurFermeture = 2.0;
                                  const largeurBandeMin = 3.0;
                                  const espaceDisponible = dimensionSecondaire - largeurFermeture;
                                  const grilleLargeurIdeal = espaceDisponible * 0.8;
                                  const espaceBandes = espaceDisponible - grilleLargeurIdeal;
                                  const largeurBande = Math.max(largeurBandeMin, espaceBandes / 3);
                                  
                                  return (
                                    <span><strong>3 bandes :</strong> Matière dessous ({dimensionPrincipale}cm × {largeurBande.toFixed(1)}cm)</span>
                                  );
                                })()}
                              </div>
                              <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 bg-white border-2 border-gray-400 flex-shrink-0"></div>
                                <span><strong>Mesh central :</strong> Grille de drainage démontable</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 bg-white border-2 border-gray-500 flex-shrink-0"></div>
                                <span><strong>Fermeture :</strong> {Math.max(article.dimensions.longueur || 50, article.dimensions.largeur || 30)}cm × 2.0cm ({article.options.fermeture})</span>
                              </div>
                            </>
                          )}
                        </div>
                        
                        {/* Informations techniques */}
                        <div className="mt-2 p-2 bg-blue-50 rounded border-l-4 border-blue-400">
                          <div className="text-xs text-blue-800 space-y-0.5">
                            <div><strong>Fonction :</strong> Évacuation eau + ventilation </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="surpiqure"
                      checked={article.options.surpiqure}
                      onChange={(e) => {
                        handleChange(e, 'options');
                        onDataChange();
                      }}
                      className="mr-2"
                    />
                    Surpiqûre
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="formeComplexe"
                      checked={article.options.formeComplexe}
                      onChange={(e) => {
                        handleChange(e, 'options');
                        onDataChange();
                      }}
                      className="mr-2"
                    />
                    Forme complexe
                  </label>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="flex items-center mb-1">
                      <input
                        type="checkbox"
                        name="mousse"
                        checked={!!article.options.mousse}
                        onChange={(e) => {
                          const event = {
                            target: { 
                              name: 'mousse', 
                              value: e.target.checked ? (mousses.length > 0 ? mousses[0].id : 'souple') : '' 
                            }
                          } as React.ChangeEvent<HTMLSelectElement>;
                          handleChange(event, 'options');
                          onDataChange();
                        }}
                        className="mr-2"
                      />
                      Mousse
                    </label>
                    {article.options.mousse && (
                      <select
                        name="mousse"
                        value={article.options.mousse}
                        onChange={(e) => {
                          handleChange(e, 'options');
                          onDataChange();
                        }}
                        className="w-full px-3 py-1 border border-gray-300 rounded text-sm"
                        disabled={moussesLoading}
                      >
                        {moussesLoading ? (
                          <option value="">Chargement...</option>
                        ) : mousses.length > 0 ? (
                          mousses.map((mousse) => (
                            <option key={mousse.id} value={mousse.id}>
                              {mousse.nom} - {mousse.prixM3.toFixed(0)} €/m³
                            </option>
                          ))
                        ) : (
                          // Fallback sur les anciens types si les mousses ne se chargent pas
                          <>
                            <option value="souple">Souple</option>
                            <option value="ferme">Ferme</option>
                            <option value="tres_ferme">Très ferme</option>
                          </>
                        )}
                      </select>
                    )}
                  </div>
                  <div>
                    <label className="flex items-center mb-1">
                      <input
                        type="checkbox"
                        name="fermeture"
                        checked={!!article.options.fermeture}
                        onChange={(e) => {
                          const event = {
                            target: { 
                              name: 'fermeture', 
                              value: e.target.checked ? 'glissiere' : '' 
                            }
                          } as React.ChangeEvent<HTMLSelectElement>;
                          handleChange(event, 'options');
                          onDataChange();
                        }}
                        className="mr-2"
                      />
                      Fermeture
                    </label>
                    {article.options.fermeture && (
                      <select
                        name="fermeture"
                        value={article.options.fermeture}
                        onChange={(e) => {
                          handleChange(e, 'options');
                          onDataChange();
                        }}
                        className="w-full px-3 py-1 border border-gray-300 rounded text-sm"
                      >
                        <option value="">Aucune</option>
                        <option value="glissiere">Fermeture à glissière</option>
                        <option value="velcro">Velcro</option>
                      </select>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="oeillets"
                      checked={article.options.oeillets}
                      onChange={(e) => {
                        handleChange(e, 'options');
                        onDataChange();
                      }}
                      className="mr-2"
                    />
                    Œillets
                  </label>
                  {article.options.oeillets && (
                    <div className="ml-6">
                      <label className="block text-sm text-gray-600 mb-1">
                        Espacement (cm)
                      </label>
                      <input
                        type="number"
                        name="espacementOeillets"
                        value={article.options.espacementOeillets || 30}
                        onChange={(e) => {
                          handleChange(e, 'options');
                          onDataChange();
                        }}
                        className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                        min="10"
                        max="100"
                      />
                    </div>
                  )}
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="renforts"
                      checked={article.options.renforts}
                      onChange={(e) => {
                        handleChange(e, 'options');
                        onDataChange();
                      }}
                      className="mr-2"
                    />
                    Renforts aux angles
                  </label>
                  {article.options.renforts && (
                    <div className="ml-6">
                      <label className="block text-sm text-gray-600 mb-1">
                        Nombre
                      </label>
                      <input
                        type="number"
                        name="nombreRenforts"
                        value={article.options.nombreRenforts || 4}
                        onChange={(e) => {
                          handleChange(e, 'options');
                          onDataChange();
                        }}
                        className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                        min="1"
                        max="8"
                      />
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="sangles"
                      checked={article.options.sangles}
                      onChange={(e) => {
                        handleChange(e, 'options');
                        onDataChange();
                      }}
                      className="mr-2"
                    />
                    Sangles
                  </label>
                  {article.options.sangles && (
                    <div className="ml-6">
                      <label className="block text-sm text-gray-600 mb-1">
                        Nombre
                      </label>
                      <input
                        type="number"
                        name="nombreSangles"
                        value={article.options.nombreSangles || 2}
                        onChange={(e) => {
                          handleChange(e, 'options');
                          onDataChange();
                        }}
                        className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                        min="1"
                        max="6"
                      />
                    </div>
                  )}
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="assemblageAuto"
                      checked={article.options.assemblageAuto}
                      onChange={(e) => {
                        handleChange(e, 'options');
                        onDataChange();
                      }}
                      className="mr-2"
                    />
                    Assemblage automatique
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="formeComplexe"
                      checked={article.options.formeComplexe}
                      onChange={(e) => {
                        handleChange(e, 'options');
                        onDataChange();
                      }}
                      className="mr-2"
                    />
                    Forme complexe
                  </label>
                </div>
              </div>
            )}
            </div>
          )}
        </div>
      </div>

      {/* Modal d'aperçu agrandi */}
      {showLargePreview && article.options.grille && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6">
              {/* En-tête de la modal */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  🔍 Aperçu détaillé - Composition du fond {isFormeRonde(article.forme) ? 'circulaire' : 'rectangulaire'}
                </h3>
                <button
                  onClick={() => setShowLargePreview(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Aperçu agrandi */}
              <div className="bg-white border-2 border-gray-300 rounded-lg p-4 mb-4">
                {isFormeRonde(article.forme) ? (
                  // === FORME RONDE AGRANDIE ===
                  <div className="flex items-center justify-center bg-gray-100 rounded-lg border border-gray-400" style={{height: '400px'}}>
                    <div className="relative">
                      {/* Fond circulaire complet en mesh - VERSION AGRANDIE */}
                      <div className="w-80 h-80 bg-gray-200 border-4 border-gray-500 rounded-full shadow-lg relative">
                        {/* Motif mesh circulaire agrandi */}
                        <div className="absolute inset-4 rounded-full overflow-hidden">
                          <div className="grid grid-cols-12 grid-rows-12 gap-1 h-full">
                            {Array.from({length: 144}).map((_, i) => (
                              <div key={i} className="bg-gray-400 rounded-full shadow-sm"></div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Fermeture en arc qui suit le périmètre du mesh - 1/2 périmètre en haut - VERSION AGRANDIE */}
                        {(() => {
                          const diametre = article.dimensions.diametre || 50;
                          const largeurFermeture = 2.0; // 2cm
                          
                          // Le cercle mesh fait 304px (w-80 h-80 avec inset-4), donc rayon = 152px
                          const rayonMesh = 152; // (320px - 16px) / 2
                          
                          // CORRECTION : Arc qui suit le périmètre du mesh - 1/2 périmètre - VERSION AGRANDIE
                          const rayonFermeture = rayonMesh; // Même rayon que le mesh
                          const epaisseurFAG = Math.max(8, (largeurFermeture / diametre) * (rayonMesh * 2)); // Épaisseur FAG en pixels (min 8px pour version agrandie)
                          
                          return (
                            <div 
                              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10"
                              style={{
                                width: `${rayonFermeture * 2}px`,
                                height: `${rayonFermeture * 2}px`
                              }}
                            >
                              {/* Demi-cercle supérieur pour la FAG - VERSION AGRANDIE */}
                              <div 
                                className="absolute"
                                style={{
                                  top: '0px',
                                  left: '0px',
                                  width: `${rayonFermeture * 2}px`,
                                  height: `${rayonFermeture}px`,
                                  borderTop: `${epaisseurFAG}px solid #fbbf24`,
                                  borderLeft: `${epaisseurFAG}px solid #fbbf24`,
                                  borderRight: `${epaisseurFAG}px solid #fbbf24`,
                                  borderTopLeftRadius: `${rayonFermeture}px`,
                                  borderTopRightRadius: `${rayonFermeture}px`,
                                  borderBottom: 'none'
                                }}
                              ></div>
                            </div>
                          );
                        })()}
                        
                        {/* Label central agrandi */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-2xl text-gray-700 font-bold bg-white/90 px-4 py-2 rounded-lg shadow">
                            Mesh Drainage
                          </span>
                        </div>
                      </div>
                      
                      {/* Labels agrandis */}
                      <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 text-lg text-gray-700 whitespace-nowrap font-bold">
                        Fond mesh complet - ⌀{article.dimensions.diametre || 50}cm
                      </div>
                      <div className="absolute -top-16 left-1/2 transform -translate-x-1/2 text-lg text-yellow-700 font-bold whitespace-nowrap">
                        Fermeture {article.options.fermeture} - 1/2 périmètre
                      </div>
                    </div>
                  </div>
                ) : (
                  // === FORMES RECTANGULAIRES AGRANDIES ===
                  <div className="flex flex-col bg-gray-100 rounded-lg border border-gray-400 overflow-hidden" style={{height: '400px'}}>
                    {(() => {
                      // CALCULS CORRECTS SELON LOGIQUE MÉTIER
                      const longueur = article.dimensions.longueur || 60;
                      const largeur = article.dimensions.largeur || 30;
                      
                      // Dimension principale et secondaire
                      const dimensionPrincipale = Math.max(longueur, largeur);
                      const dimensionSecondaire = Math.min(longueur, largeur);
                      
                      // Calculs corrects : bandes minimum 3cm (même logique que backend)
                      const largeurFermeture = 2.0; // Fixe : 2cm
                      const largeurBandeMin = 3.0; // Minimum : 3cm par bande
                      
                      // Calculer la largeur des bandes : minimum 3cm, mais peut être plus si nécessaire
                      const espaceDisponible = dimensionSecondaire - largeurFermeture; // 60 - 2 = 58cm
                      const grilleLargeurIdeal = espaceDisponible * 0.8; // 80% de l'espace disponible = 46.4cm
                      const espaceBandes = espaceDisponible - grilleLargeurIdeal; // 58 - 46.4 = 11.6cm
                      const largeurBande = Math.max(largeurBandeMin, espaceBandes / 3); // max(3, 3.87) = 3.87cm
                      
                      // Recalculer la grille avec les bandes minimum
                      const grilleLargeur = dimensionSecondaire - (3 * largeurBande) - largeurFermeture;
                      
                      // Hauteur totale = dimension secondaire originale (30cm)
                      const hauteurTotale = dimensionSecondaire;
                      
                      // Proportions exactes en %
                      const propBande1 = (largeurBande / hauteurTotale) * 100;
                      const propGrille = (grilleLargeur / hauteurTotale) * 100;
                      const propBande2 = (largeurBande / hauteurTotale) * 100;
                      const propFermeture = (largeurFermeture / hauteurTotale) * 100;
                      const propBande3 = (largeurBande / hauteurTotale) * 100;
                      
                      return (
                        <>
                          {/* Bande fond 1 - Proportion exacte backend */}
                          <div className="flex items-center justify-center bg-teal-400 border-b border-teal-600 text-white font-bold text-lg" 
                               style={{height: `${propBande1}%`, minHeight: '25px'}}>
                            <span>Bande 1 ({largeurBande.toFixed(1)}cm)</span>
                          </div>
                          
                          {/* Mesh central - 80% dimension secondaire */}
                          <div className="flex flex-col items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 border-b-2 border-gray-400 relative overflow-hidden" 
                               style={{height: `${propGrille}%`, minHeight: '200px'}}>
                            {/* Motif grille réaliste avec lignes horizontales et verticales */}
                            <div className="relative w-full h-full p-2">
                              {/* Fond métallique */}
                              <div className="w-full h-full bg-gradient-to-br from-slate-300 to-slate-400 rounded-sm"></div>
                              
                              {/* Lignes horizontales de la grille */}
                              {Array.from({length: 9}).map((_, i) => (
                                <div 
                                  key={`h-${i}`} 
                                  className="absolute left-2 right-2 h-0.5 bg-slate-600 shadow-sm"
                                  style={{top: `${8 + (i * 10.5)}%`}}
                                ></div>
                              ))}
                              
                              {/* Lignes verticales de la grille */}
                              {Array.from({length: 17}).map((_, i) => (
                                <div 
                                  key={`v-${i}`} 
                                  className="absolute top-2 bottom-2 w-0.5 bg-slate-600 shadow-sm"
                                  style={{left: `${8 + (i * 5.25)}%`}}
                                ></div>
                              ))}
                            </div>
                            <span className="absolute bottom-2 text-lg text-gray-700 font-medium bg-white/90 px-2 rounded shadow">
                              Mesh drainage ({grilleLargeur.toFixed(1)}cm)
                            </span>
                          </div>
                          
                          {/* Bande fond 2 - Même proportion que Bande 1 */}
                          <div className="flex items-center justify-center bg-teal-400 border-b border-teal-600 text-white font-bold text-lg" 
                               style={{height: `${propBande2}%`, minHeight: '25px'}}>
                            <span>Bande 2 ({largeurBande.toFixed(1)}cm)</span>
                          </div>
                          
                          {/* Fermeture - 2cm fixe - MÊME LONGUEUR QUE LES BANDES */}
                          <div className="flex items-center bg-white border-b-2 border-gray-500 relative" 
                               style={{height: `${propFermeture}%`, minHeight: '20px'}}>
                            {/* Dents de fermeture s'étendant sur TOUTE la largeur */}
                            <div className="flex flex-row justify-between items-center w-full h-full px-2">
                              {Array.from({length: 40}).map((_, i) => (
                                <div key={i} className="w-1 h-3 bg-gray-400 rounded"></div>
                              ))}
                            </div>
                            <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-lg text-gray-700 font-medium bg-white px-2 rounded">
                              Fermeture Zip ({largeurFermeture}cm)
                            </span>
                          </div>
                          
                          {/* Bande fond 3 - Même proportion que Bandes 1 et 2 */}
                          <div className="flex items-center justify-center bg-teal-400 text-white font-bold text-lg rounded-b-lg" 
                               style={{height: `${propBande3}%`, minHeight: '25px'}}>
                            <span>Bande 3 ({largeurBande.toFixed(1)}cm)</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Informations détaillées */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Légende détaillée */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Composition détaillée</h4>
                  <div className="space-y-2">
                    {isFormeRonde(article.forme) ? (
                      // === LÉGENDE FORME RONDE ===
                      <>
                        <div className="flex items-center space-x-3">
                          <div className="w-4 h-4 bg-gray-400 border border-gray-500 rounded-full"></div>
                          <span className="text-sm"><strong>Fond mesh complet :</strong> Matière mesh - ⌀{article.dimensions.diametre || 50}cm</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className="w-4 h-4 bg-yellow-300 border border-yellow-500"></div>
                          <span className="text-sm"><strong>Fermeture à glissière :</strong> {article.options.fermeture} sur 1/2 périmètre</span>
                        </div>
                      </>
                    ) : (
                      // === LÉGENDE FORMES RECTANGULAIRES ===
                      (() => {
                        const longueur = article.dimensions.longueur || 60;
                        const largeur = article.dimensions.largeur || 30;
                        const dimensionPrincipale = Math.max(longueur, largeur);
                        const dimensionSecondaire = Math.min(longueur, largeur);
                        
                        // Calculs corrects : bandes minimum 3cm (même logique que backend)
                        const largeurFermeture = 2.0;
                        const largeurBandeMin = 3.0;
                        const espaceDisponible = dimensionSecondaire - largeurFermeture;
                        const grilleLargeurIdeal = espaceDisponible * 0.8;
                        const espaceBandes = espaceDisponible - grilleLargeurIdeal;
                        const largeurBande = Math.max(largeurBandeMin, espaceBandes / 3);
                        const grilleLargeur = dimensionSecondaire - (3 * largeurBande) - largeurFermeture;
                        
                        return (
                          <>
                            <div className="flex items-center space-x-3">
                              <div className="w-4 h-4 bg-teal-400 border border-teal-600 rounded"></div>
                              <span className="text-sm"><strong>3 bandes :</strong> Matière dessous ({dimensionPrincipale}cm × {largeurBande.toFixed(1)}cm chacune)</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <div className="w-4 h-4 bg-white border-2 border-gray-400"></div>
                              <span className="text-sm"><strong>Mesh central :</strong> Grille de drainage démontable ({dimensionPrincipale}cm × {grilleLargeur.toFixed(1)}cm)</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <div className="w-4 h-4 bg-white border-2 border-gray-500"></div>
                              <span className="text-sm"><strong>Fermeture :</strong> {dimensionPrincipale}cm × {largeurFermeture.toFixed(1)}cm ({article.options.fermeture})</span>
                            </div>
                          </>
                        );
                      })()
                    )}
                  </div>
                </div>

                {/* Informations techniques */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Informations techniques</h4>
                  <div className="space-y-2 text-sm">
                    <div><strong>Fonction :</strong> Évacuation eau + ventilation</div>
                    {isFormeRonde(article.forme) ? (
                      <div><strong>Dimension totale :</strong> ⌀{article.dimensions.diametre || 50}cm</div>
                    ) : (
                      (() => {
                        const longueur = article.dimensions.longueur || 60;
                        const largeur = article.dimensions.largeur || 30;
                        const dimensionPrincipale = Math.max(longueur, largeur);
                        const dimensionSecondaire = Math.min(longueur, largeur);
                        
                        return (
                          <div><strong>Dimension totale :</strong> {dimensionPrincipale}cm × {dimensionSecondaire}cm</div>
                        );
                      })()
                    )}
                  </div>
                </div>
              </div>

              {/* Bouton de fermeture */}
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowLargePreview(false)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticleConfigurationCompact;
